import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import {
    Add,
    CloseCircle,
    Copy,
    Edit2,
    Link1,
    Profile2User,
    ScanBarcode,
    Send2,
    Trash,
} from "iconsax-react";
import RowActionButton from "../../pages/surveys/RowActionButton";
import DBButton from "../../component/global/DBButton";
import DBModal from "../../component/global/DBModal";
import DBTable from "../../component/global/DBTable/DBTable";
import DBSpinner from "../../component/global/DBSpinner";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SURVEY_CAMPAIGNS,
    surveyCampaignUrl,
    surveyPublicPageUrl,
} from "../../helperData/ApiAddress";
import {
    CAMPAIGN_STATUS,
    campaignIsCancellable,
    campaignIsDeletable,
    campaignIsSendable,
    campaignKindLabel,
    campaignStatusLabel,
    isLinkCampaign,
} from "../../pages/surveys/constants";
import CampaignModal from "../../pages/surveys/CampaignModal";
import RecipientsModal from "../../pages/surveys/RecipientsModal";
import { ADDRESS_CAMPAIGN_KINDS } from "../../pages/surveys/CampaignRecipientsSection";
import { translateServerDetail } from "../../pages/surveys/translateServerDetail";
import { campaignSendQuoteSpec } from "../../pages/surveys/sendBilling";
import {
    isInsufficientCoins,
    useAiCoinGate,
} from "../../features/ai-billing/AiCoinGate";
import { contentStudioApi, errorMessage } from "./api";
import { CONTENT_STATUS } from "./constants";
import "../../pages/surveys/style.scss";

export default function ContentCampaignsPanel() {
    const toast = useContext(ToastContext);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignModal, setCampaignModal] = useState({
        show: false,
        campaign: null,
    });
    const [recipientsModal, setRecipientsModal] = useState(null);
    const [confirmState, setConfirmState] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [contentPick, setContentPick] = useState(null);
    const [approvedContents, setApprovedContents] = useState([]);
    const [approvedLoading, setApprovedLoading] = useState(false);
    const [selectedContentId, setSelectedContentId] = useState("");
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    const [getCampaigns, getCampaignsLoading] = useRequest(
        SURVEY_CAMPAIGNS,
        "GET"
    );

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
        "Accept-Language": i18n.language || "fa",
    });

    const loadCampaigns = () => {
        getCampaigns({ page_size: 100 })
            .then((r) => setCampaigns(r.results || []))
            .catch(() => setCampaigns([]));
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    useEffect(() => {
        if (!contentPick) return;
        setApprovedLoading(true);
        contentStudioApi
            .list({
                status: CONTENT_STATUS.CONFIRMED,
                page_size: 100,
            })
            .then((data) => {
                const rows = data.rows || [];
                setApprovedContents(rows);
                const preset =
                    contentPick.campaign?.content ||
                    (rows.length === 1 ? rows[0].id : "");
                setSelectedContentId(preset ? String(preset) : "");
            })
            .catch((error) => {
                setApprovedContents([]);
                toast(
                    errorMessage(error, t("contentStudio.errors.load")),
                    "danger"
                );
            })
            .finally(() => setApprovedLoading(false));
    }, [contentPick, toast]);

    const postAction = (campaign, action, data = {}) =>
        axios.post(surveyCampaignUrl(campaign.id, action), data, {
            headers: authHeaders(),
        });

    const executeAction = (campaign, action, data = {}) => {
        setActionLoading(true);
        return postAction(campaign, action, data)
            .then(() => {
                if (action === "send") {
                    toast(t("survey.campaign.send.started"), "success");
                } else {
                    toast(t("survey.campaign.save.success"), "success");
                }
                setConfirmState(null);
                setContentPick(null);
                loadCampaigns();
            })
            .catch((e) => {
                if (action === "send" && isInsufficientCoins(e)) {
                    openFromError(e, () =>
                        executeAction(campaign, action, data)
                    );
                    return;
                }
                const detail = e.response?.data?.detail;
                const translated = translateServerDetail(detail, t);
                toast(
                    translated ||
                        (action === "send"
                            ? t("survey.campaign.send.error")
                            : t("survey.campaign.cancel.error.generic")),
                    "danger"
                );
                setConfirmState(null);
            })
            .finally(() => setActionLoading(false));
    };

    const queueSend = (campaign, contentId) => {
        const payload = { content: Number(contentId) };
        const spec = campaignSendQuoteSpec(campaign);
        if (spec) {
            confirmAndRun(spec, () =>
                executeAction(campaign, "send", payload)
            );
            return;
        }
        setConfirmState({
            campaign,
            action: "send",
            content: payload.content,
        });
        setContentPick(null);
    };

    const handleConfirm = () => {
        const { campaign, action, content } = confirmState;
        if (action === "delete") {
            handleDelete(campaign);
            return;
        }
        executeAction(campaign, action, content ? { content } : {});
    };

    const handleDelete = (campaign) => {
        setActionLoading(true);
        axios
            .delete(`${SURVEY_CAMPAIGNS}${campaign.id}/`, {
                headers: authHeaders(),
            })
            .then(() => {
                toast(t("survey.campaign.delete.success"), "success");
                setConfirmState(null);
                loadCampaigns();
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.campaign.delete.error"), "danger");
                setConfirmState(null);
            })
            .finally(() => setActionLoading(false));
    };

    const handleDuplicate = (campaign) => {
        postAction(campaign, "duplicate")
            .then(() => {
                toast(t("survey.campaign.duplicate.success"), "success");
                loadCampaigns();
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                toast(detail || t("survey.campaign.duplicate.error"), "danger");
            });
    };

    const handleCopyLink = (campaign) => {
        const url = surveyPublicPageUrl(campaign.public_url);
        navigator.clipboard
            ?.writeText(url)
            .then(() => toast(t("survey.campaign.link.copied"), "success"))
            .catch(() => toast(t("survey.campaign.link.copied"), "success"));
    };

    const handleQr = (campaign) => {
        axios
            .get(surveyCampaignUrl(campaign.id, "qr-code"), {
                headers: authHeaders(),
                responseType: "blob",
            })
            .then((r) => {
                const url = URL.createObjectURL(r.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = `content-campaign-${campaign.id}.png`;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => toast(t("survey.campaign.send.error"), "danger"));
    };

    const openCampaignModal = (campaign = null) => {
        setCampaignModal({ show: true, campaign });
    };

    const headers = [
        {
            id: "name",
            title: t("survey.campaign.table.name"),
            gridTemplateColumns: "1.3fr",
        },
        {
            id: "contentTitle",
            title: t("contentStudio.send.contentColumn"),
            gridTemplateColumns: "1.2fr",
        },
        {
            id: "surveyTitle",
            title: t("survey.campaign.table.survey"),
            gridTemplateColumns: "1fr",
        },
        {
            id: "kind",
            title: t("survey.campaign.table.kind"),
            gridTemplateColumns: "1fr",
        },
        {
            id: "status",
            title: t("survey.campaign.table.status"),
            gridTemplateColumns: "0.9fr",
        },
        {
            id: "recipients",
            title: t("survey.campaign.table.recipients"),
            gridTemplateColumns: "0.7fr",
        },
        {
            id: "sent",
            title: t("survey.campaign.table.sent"),
            gridTemplateColumns: "0.7fr",
        },
        {
            id: "actions",
            title: t("survey.campaign.table.actions"),
            gridTemplateColumns: "minmax(176px, max-content)",
        },
    ];

    const rows = campaigns.map((campaign) => ({
        name: campaign.name,
        contentTitle: campaign.content_title || "—",
        surveyTitle: campaign.survey_title || "—",
        kind: campaignKindLabel(campaign.kind, campaign.user_group_type),
        status: campaignStatusLabel(campaign.status),
        recipients: campaign.recipients_count,
        sent: campaign.sent_count,
        actions: (
            <div className="survey-row-actions">
                {isLinkCampaign(campaign.kind) && campaign.content && (
                    <RowActionButton
                        icon={Link1}
                        label={t("survey.campaign.action.copy.link")}
                        onClick={() => handleCopyLink(campaign)}
                    />
                )}
                {isLinkCampaign(campaign.kind) && campaign.content && (
                    <RowActionButton
                        icon={ScanBarcode}
                        label={t("survey.campaign.action.qr")}
                        onClick={() => handleQr(campaign)}
                    />
                )}
                {ADDRESS_CAMPAIGN_KINDS.includes(campaign.kind) && (
                    <RowActionButton
                        icon={Profile2User}
                        label={t("survey.campaign.action.recipients")}
                        onClick={() => setRecipientsModal(campaign)}
                    />
                )}
                <RowActionButton
                    icon={Copy}
                    label={t("survey.campaign.action.copy")}
                    onClick={() => handleDuplicate(campaign)}
                />
                {campaignIsSendable(campaign) && (
                    <>
                        <RowActionButton
                            icon={Edit2}
                            label={t("survey.action.edit")}
                            onClick={() => openCampaignModal(campaign)}
                        />
                        <RowActionButton
                            icon={Send2}
                            label={
                                campaign.status === CAMPAIGN_STATUS.SENT
                                    ? t("survey.campaign.action.resend")
                                    : t("survey.campaign.action.send")
                            }
                            onClick={() => setContentPick({ campaign })}
                        />
                    </>
                )}
                {campaignIsCancellable(campaign) && (
                    <RowActionButton
                        icon={CloseCircle}
                        danger
                        label={t("survey.campaign.action.cancel")}
                        onClick={() =>
                            setConfirmState({ campaign, action: "cancel" })
                        }
                    />
                )}
                {campaignIsDeletable(campaign) && (
                    <RowActionButton
                        icon={Trash}
                        danger
                        label={t("survey.campaign.action.delete")}
                        onClick={() =>
                            setConfirmState({ campaign, action: "delete" })
                        }
                    />
                )}
            </div>
        ),
    }));

    return (
        <div className="studio-send campaigns-panel surveys-page">
            <p className="studio-muted">{t("contentStudio.send.lead")}</p>
            <div className="surveys-toolbar">
                <DBButton
                    caption={
                        <>
                            <Add size={18} aria-hidden="true" />{" "}
                            {t("contentStudio.send.createCampaign")}
                        </>
                    }
                    onClick={() => openCampaignModal(null)}
                />
            </div>
            {getCampaignsLoading ? (
                <DBSpinner tip={t("loading")} />
            ) : campaigns.length === 0 ? (
                <div className="surveys-empty">
                    {t("contentStudio.send.empty")}
                </div>
            ) : (
                <div className="surveys-table-wrap">
                    <DBTable minWidth={980} headers={headers} rows={rows} />
                </div>
            )}

            <CampaignModal
                show={campaignModal.show}
                mode="content"
                campaign={campaignModal.campaign}
                onHide={() =>
                    setCampaignModal({ show: false, campaign: null })
                }
                onSuccess={(nextCampaign, options = {}) => {
                    loadCampaigns();
                    if (options.keepOpen && nextCampaign?.id) {
                        setCampaignModal({
                            show: true,
                            campaign: nextCampaign,
                        });
                        return;
                    }
                    setCampaignModal({ show: false, campaign: null });
                }}
            />
            {recipientsModal && (
                <RecipientsModal
                    campaign={recipientsModal}
                    onHide={() => setRecipientsModal(null)}
                    onChange={loadCampaigns}
                />
            )}
            <DBModal
                show={Boolean(contentPick)}
                onHide={() => setContentPick(null)}
                minWidth={480}
            >
                <DBModal.Header
                    title={t("contentStudio.send.pickTitle")}
                    closeBtn={() => setContentPick(null)}
                />
                <DBModal.Content>
                    <p className="studio-muted">
                        {t("contentStudio.send.pickLead")}
                    </p>
                    {approvedLoading ? (
                        <DBSpinner tip={t("loading")} />
                    ) : approvedContents.length === 0 ? (
                        <p>{t("contentStudio.send.noApproved")}</p>
                    ) : (
                        <label className="studio-send-pick">
                            <span>
                                {t("contentStudio.send.pickPlaceholder")}
                            </span>
                            <select
                                value={selectedContentId}
                                onChange={(event) =>
                                    setSelectedContentId(event.target.value)
                                }
                            >
                                <option value="">
                                    {t("contentStudio.send.pickPlaceholder")}
                                </option>
                                {approvedContents.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    <div className="studio-send-pick-actions">
                        <DBButton
                            type="secondary"
                            caption={t("cancel")}
                            onClick={() => setContentPick(null)}
                        />
                        <DBButton
                            caption={t("contentStudio.send.confirmSend")}
                            disabled={!selectedContentId || approvedLoading}
                            onClick={() =>
                                queueSend(
                                    contentPick.campaign,
                                    selectedContentId
                                )
                            }
                        />
                    </div>
                </DBModal.Content>
            </DBModal>
            <DBConfirmModal
                show={!!confirmState}
                onHide={() => setConfirmState(null)}
                title={
                    confirmState?.action === "delete"
                        ? t("survey.campaign.delete.confirm.title")
                        : confirmState?.action === "send"
                        ? confirmState.campaign?.status === CAMPAIGN_STATUS.SENT
                            ? t("survey.campaign.resend.confirm.title")
                            : t("survey.campaign.send.confirm.title")
                        : t("survey.campaign.cancel.confirm.title")
                }
                description={
                    confirmState?.action === "delete"
                        ? t("survey.campaign.delete.confirm.description")
                        : confirmState?.action === "send"
                        ? confirmState.campaign?.status === CAMPAIGN_STATUS.SENT
                            ? t("survey.campaign.resend.confirm.description")
                            : t("survey.campaign.send.confirm.description")
                        : t("survey.campaign.cancel.confirm.description")
                }
                btnTitle={
                    confirmState?.action === "delete"
                        ? t("survey.campaign.action.delete")
                        : confirmState?.action === "send" &&
                          confirmState.campaign?.status === CAMPAIGN_STATUS.SENT
                        ? t("survey.campaign.action.resend")
                        : t("save")
                }
                loading={actionLoading}
                onConfirm={handleConfirm}
            />
            {gate}
        </div>
    );
}
